import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { NotificationService } from '@p2p/shared/domain';
import { catchError, map, of, switchMap, take, tap } from 'rxjs';

import { PaymentProfilesDataService } from '../../infrastructure';

import * as ProfilesActions from './profiles.actions';
import { ProfilesFacade } from './profiles.facade';
import { ProfilesPageParams } from './profiles.model';

@Injectable()
export class ProfilesEffects {
    private readonly actions$ = inject(Actions);
    private readonly profilesFacade = inject(ProfilesFacade);
    private readonly paymentProfilesDataService = inject(PaymentProfilesDataService);
    private readonly notificationService = inject(NotificationService);
    private readonly translocoService = inject(TranslocoService);

    loadPaymentProfiles$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfilesActions.loadPaymentProfiles),
            switchMap(({ pageParams: newPageParams }) =>
                this.profilesFacade.pageParams$.pipe(
                    map((oldPageParams) => {
                        const pageParams: ProfilesPageParams = { ...oldPageParams, ...newPageParams };

                        Object.keys(pageParams).forEach((key) => {
                            const paramsKey = key as keyof ProfilesPageParams;

                            if (pageParams[paramsKey] == undefined || pageParams[paramsKey] === null) {
                                delete pageParams[paramsKey];
                            }
                        });

                        return pageParams;
                    }),
                    take(1)
                )
            ),
            tap((params) => this.profilesFacade.setPageParams(params)),
            switchMap((params) =>
                this.paymentProfilesDataService.getProfilesList(params).pipe(
                    map((response) => ProfilesActions.loadPaymentProfilesSuccess({ response })),
                    catchError((loadError) => of(ProfilesActions.loadPaymentProfilesFailure({ loadError })))
                )
            )
        )
    );

    deleteProfile$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfilesActions.deleteProfile),
            switchMap(({ id }) =>
                this.paymentProfilesDataService.deleteProfile(id).pipe(
                    tap(() => {
                        this.notificationService.open(
                            this.translocoService.translate('payment.Payment.Profile.Delete.Success')
                        );
                    }),
                    map(() => ProfilesActions.deleteProfileSuccess({ id })),
                    catchError((error) => of(ProfilesActions.deleteProfileFailure({ error, id })))
                )
            )
        )
    );

    changeProfileState$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfilesActions.changeProfileStatus),
            switchMap(({ id, status }) =>
                this.paymentProfilesDataService.changeProfileStatus(id, status).pipe(
                    map(() => ProfilesActions.changeProfileStatusSuccess({ id, changes: { status } })),
                    catchError((error: HttpErrorResponse) =>
                        of(ProfilesActions.changeProfileStatusFailure({ id, error }))
                    )
                )
            )
        )
    );

    createProfile$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfilesActions.createProfile),
            switchMap(({ profile }) =>
                this.paymentProfilesDataService.createProfile(profile).pipe(
                    map((profile) => {
                        this.profilesFacade.loadProfiles();

                        return ProfilesActions.createProfileSuccess({ profile });
                    }),
                    catchError((error: HttpErrorResponse) => of(ProfilesActions.createProfileFailure({ error })))
                )
            )
        )
    );

    editProfile$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfilesActions.editProfile),
            switchMap(({ profile }) =>
                this.paymentProfilesDataService.editProfile(profile).pipe(
                    map((profile) => {
                        this.profilesFacade.loadProfiles();

                        return ProfilesActions.editProfileSuccess({ profile });
                    }),
                    catchError((error: HttpErrorResponse) => of(ProfilesActions.editProfileFailure({ error })))
                )
            )
        )
    );
}
